import React, { lazy, Suspense, useCallback, useEffect, useState, memo } from "react";
import { useAppContext } from "../../../Context/AppContext";
import { Button } from "react-bootstrap";
import { useGetMITerms } from "../../../api/queries/MuslimInvest/useGetMITerms";
import { ProfileType } from "../../Models/ProfileType";
import { UserFormData } from "../../Models/UserFormData";
import { TermsFormValues } from "../../../types/PitchTypes/PitchTypes";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveInvestor } from "../../../api/mutations/MuslimInvest/useSaveInvestor";
import { useGetInvestorDetails } from "../../../api/queries/MuslimInvest/useGetInvestorDetails";
import { useSaveMITerms } from "../../../api/mutations/MuslimInvest/useSaveMITerms";
import { useUploadCertificationPdfs } from "../../../api/mutations/MuslimInvest/useUploadCertificationPdfs";
import { useSendEmailWithAttachments } from "../../../api/mutations/useSendEmailWithAttachments";
import { useModal } from "../../../Context/ModalContext";
import { useTranslation } from "react-i18next";
import { useProfileShellLabels } from "../useMuslimInvestFormLabels";
import MuslimInvestFormHeaderBack from "../MuslimInvestFormHeaderBack";

const Investor = lazy(() => import("./Investor"));
const InvestorTerms = lazy(() => import("./InvertorTerms/InvestorTerms"));
const FSMATerms = lazy(() => import("./InvertorTerms/FSMATerms"));
const RiskWarning = lazy(() => import("./InvertorTerms/RiskWarning"));

const LAZY_FALLBACK_STYLE_200 = { minHeight: 200 };

const InvestorProfile = memo(function InvestorProfile() {
  const { t } = useTranslation();
  const { tt: shellT } = useProfileShellLabels();
  const [loading, setLoading] = useState(false);
  const { user: userData } = useAppContext();
  const location = useLocation();
  const fromPendingEdit =
    (location.state as { fromPendingEdit?: boolean } | null)?.fromPendingEdit ===
    true;
  const [activeTab, setActiveTab] = useState(
    fromPendingEdit ? "profile-investor-details" : "profile",
  );
  const [isEditingProfileInvestorDetails, setIsEditingProfileInvestorDetails] =
    useState(true);
  const [errors] = useState<Record<string, string>>({});
  const { openModal } = useModal();
  const [userFormData, setUserFormData] = useState<UserFormData>({
    id: 0,
    name: "",
    email: "",
    phone: "",
    membershipType: "",
    termsAndConditions: 0,
    termsAndConditionsAcceptedDate: "",
    matchId: 0,
  });
  const { data: investorData } = useGetInvestorDetails({
    userId: userData?.user.id.toString() || "",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = investorData ? investorData[0] : null;
  const [riskPdfAttachment, setRiskPdfAttachment] = useState<File | null>(null);
  const [fsmaPdfAttachment, setFsmaPdfAttachment] = useState<File | null>(null);
  const [categoryAttachment, setCategoryRiskPdfAttachment] =
    useState<File | null>(null);

  const { mutate: sendAttachmentEmail } = useSendEmailWithAttachments({
    onSuccess() {},
  });

  const { mutate: uploadCertificationPdfs } = useUploadCertificationPdfs({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["muslim-invest-terms"],
      });
    },
  });

  const { data: termsData } = useGetMITerms({
    userId: userData?.user.id.toString() || "",
  });

  const terms = termsData && termsData[0];

  const { mutate: saveInvestorDetailsMutation } = useSaveInvestor({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor-data"] });
      openModal({
        description:
          "Congratulations, you are now a member of Muslim Invest, where Sharia compliant businesses connect to the right investors",
        title: "Terms Accepted",
        duration: 5000,
      });
      navigate("investor-checkout");
      // toast.success("Thank you for accepting the terms and conditions");
    },
    onError: () => {
      toast.error(t("common.unableProcessTryLater"));
    },
  });

  const saveProfileInvestorDetails = useCallback(() => {
    if (!profile) {
      toast.error(t("muslimInvest.investorProfileNotFound"));
      return;
    }

    const formData = new FormData();

    Object.entries(profile).forEach(([key, value]) => {
      if (key === "id" && typeof value === "number" && value <= 0) return;
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });

    formData.set("status", "Payment Pending");
    saveInvestorDetailsMutation(formData);
  }, [profile, saveInvestorDetailsMutation, t]);

  const storeCertificationPdfs = useCallback(() => {
    if (!terms?.id || !categoryAttachment) return;

    const pdfFiles = [
      riskPdfAttachment,
      fsmaPdfAttachment,
      categoryAttachment,
    ].filter(Boolean) as File[];

    if (pdfFiles.length === 0) return;

    uploadCertificationPdfs({
      scope: "investor",
      termsId: terms.id,
      pdfFiles,
    });
  }, [
    terms?.id,
    categoryAttachment,
    riskPdfAttachment,
    fsmaPdfAttachment,
    uploadCertificationPdfs,
  ]);

  const sendMail = useCallback(async () => {
    if (categoryAttachment) {
      sendAttachmentEmail({
        email: userFormData.email || "",
        subject: `You have accepted the SMBF / Muslim Invest Terms and Conditions`,
        name: `${userFormData.name || ""}`,
        body: `<!DOCTYPE html>
  <html lang="en">
  <head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Terms Accepted by SMBF</title>
  </head>
  <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
  <table width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
    <tr>
      <td align="center" style="padding:40px 20px;">
        <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff"
          style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
  
          <!-- Header -->
          <tr>
            <td align="center" style="background: linear-gradient(165deg, #031B3D 0%, #082F6F 55%, #02152F 100%); padding:30px;">
              <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg"
                alt="SMBF" width="180"
                style="max-width:180px; height:auto; display:block; margin:0 auto;" />
            </td>
          </tr>
  
          <!-- Body -->
          <tr>
            <td style="padding:35px; text-align:left; color:#333333; font-size:15px; line-height:1.6;">
              <h2 style="color:#075CCB; font-size:22px; margin:0 0 20px;">
                Assalamu Alaikum ${userFormData.name || ""},
              </h2>
              <p style="margin:0 0 15px;">
                Thank you for reviewing and accepting the <b>SMBF / Muslim Invest Terms and Conditions</b>.
              </p>
              <p style="margin:0 0 15px;">
                You have confirmed acceptance of the following:
              </p>
              <ul style="margin:0 0 15px; padding-left:20px;">
                <li>Risk acknowledgement</li>
                <li>FSMA status & certification</li>
                <li>Ongoing obligation</li>
                <li>No-advice confirmation</li>
              </ul>
              <p style="margin:0 0 25px;">
                You may now proceed with your investment activities as per your membership status.
              </p>
              <p style="text-align:center;">
                <a href="https://www.connect.smbf.global/dashboard" target="_blank"
                  style="background: linear-gradient(165deg, #031B3D 0%, #082F6F 55%, #02152F 100%);
                  color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none;
                  border-radius:6px; display:inline-block;">
                  Go to Dashboard
                </a>
              </p>
              <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                Best regards,<br/>The SMBF Team
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  
    <!-- Footer -->
    <tr>
      <td align="center" style="padding:20px; font-size:11px; color:#888888;">
        &copy; 2025 SMBF. All rights reserved.
      </td>
    </tr>
  </table>
  </body>
  </html>`,
        pdfFiles: [
          riskPdfAttachment,
          fsmaPdfAttachment,
          categoryAttachment,
        ].filter(Boolean) as File[],
      });
    }
  }, [
    categoryAttachment,
    userFormData.email,
    userFormData.name,
    riskPdfAttachment,
    fsmaPdfAttachment,
    sendAttachmentEmail,
  ]);

  useEffect(() => {
    if (categoryAttachment) {
      storeCertificationPdfs();
      sendMail();
      saveProfileInvestorDetails();
    }
  }, [
    categoryAttachment,
    storeCertificationPdfs,
    sendMail,
    saveProfileInvestorDetails,
  ]);

  const { mutate: saveTermsMutation } = useSaveMITerms({
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: ["muslim-invest-terms"],
      });
    },
    onError: () => {
      toast.error(t("common.unableProcessTryLater"));
    },
  });

  const saveTerms = useCallback(
    async (data: TermsFormValues) => {
      const currentDateISO = new Date().toISOString();
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "id" && typeof value === "number" && value <= 0) return;
        if (value !== undefined && value !== null)
          formData.append(key, String(value));
      });

      formData.append(
        "UserId",
        userData?.user.id ? userData.user.id.toString() : ""
      );

      if (data.finalAccept === "true") {
        formData.append("timeStamp", navigator.userAgent);
        formData.append("IsAccepted", "1");
        formData.append("TermsAndConditionsAcceptedDate", currentDateISO);
        formData.append("InvestorProfileId", profile?.id?.toString() || "0");
        formData.append("InvestmentType", data.investmentType || "");
      }

      saveTermsMutation(formData);
    },
    [userData?.user.id, profile?.id, saveTermsMutation]
  );

  const handleChange = useCallback(
    (e: any, profileType: ProfileType) => {
    const { name, value } = e.target;

    switch (profileType) {
      case ProfileType.User:
        setUserFormData({ ...userFormData, [name]: value });
        break;

      default:
        break;
    }
    },
    [userFormData]
  );

  useEffect(() => {
    const user = userData?.user;

    if (user) {
      setUserFormData((prev) => ({
        ...prev,
        id: user.id ?? prev.id,
        name: String(user.firstName ?? "") + String(user.lastName ?? ""),
        email: user.email ?? prev.email,
        phone: user.businessTelephoneNumber ?? prev.phone,
        membershipType: user.membershipName ?? prev.membershipType,
        termsAndConditions: terms?.isAccepted ? 1 : 0,
        termsAndConditionsAcceptedDate:
          terms?.dateAccepted ?? prev.termsAndConditionsAcceptedDate,
        matchId: prev.matchId,
      }));
    }
  }, [termsData, userData, terms?.isAccepted, terms?.dateAccepted]);

  const viewProfileInvestorDetails = useCallback(() => {
    setActiveTab((prevTab) => {
      if (prevTab !== "profile-investor-details") {
        return "profile-investor-details";
      }
      return prevTab;
    });
  }, []);

  const viewProfileTermsAndConditions = useCallback(() => {
    setActiveTab((prevTab) => {
      if (prevTab !== "profile-terms-conditions") {
        return "profile-terms-conditions";
      }
      return prevTab;
    });
    setIsEditingProfileInvestorDetails(false);
  }, [setIsEditingProfileInvestorDetails]);

  const handleUserChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleChange(e, ProfileType.User),
    [handleChange]
  );

  const handleViewInvestorDetails = useCallback(
    () => viewProfileInvestorDetails(),
    [viewProfileInvestorDetails]
  );

  return (
    <div>
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="dashboard content -mx-2">
        <div className="container-fluid px-0 pb-4">
          <div className="row g-0">
            <div className="col-lg-12">
              {activeTab === "profile" && (
                <div className="card shadow-sm border-0">
                  <div className="d-flex card-header bg-project-gradient text-white justify-content-between align-items-center">
                    <div className="d-flex align-items-center min-w-0">
                      <MuslimInvestFormHeaderBack />
                      <h4 className="mb-0">{shellT("profile")}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-2">{shellT("name")}</label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("name")}
                        value={userFormData.name || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("email")}</label>
                      <input
                        type="text"
                        name="email"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("email")}
                        value={userFormData.email || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("phone")}</label>
                      <input
                        type="text"
                        name="phone"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("phone")}
                        value={userFormData.phone || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("membershipType")}</label>
                      <input
                        type="text"
                        name="membershipType"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("membershipType")}
                        value={userFormData.membershipType || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="d-flex justify-content-end w-100">
                      <Button type="button" onClick={handleViewInvestorDetails}>
                        {shellT("next")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "profile-investor-details" && (
                <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_200} aria-hidden="true" />}>
                  <Investor
                    setLoading={setLoading}
                    isEditingProfileInvestorDetails={
                      isEditingProfileInvestorDetails
                    }
                    setIsEditingProfileInvestorDetails={
                      setIsEditingProfileInvestorDetails
                    }
                    viewProfileTermsAndConditions={viewProfileTermsAndConditions}
                    setActiveTab={setActiveTab}
                  />
                </Suspense>
              )}
              {/* Terms and Conditions */}
              {activeTab === "risk-agreement" && (
                <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_200} aria-hidden="true" />}>
                  <RiskWarning
                    setRiskPdfAttachment={setRiskPdfAttachment}
                    setActiveTab={setActiveTab}
                    saveTerms={saveTerms}
                    terms={terms || ({} as TermsFormValues)}
                  />
                </Suspense>
              )}
              {activeTab === "fsma-terms" && terms?.id && (
                <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_200} aria-hidden="true" />}>
                  <FSMATerms
                    setActiveTab={setActiveTab}
                    saveTerms={saveTerms}
                    terms={terms || ({} as TermsFormValues)}
                    setFsmaPdfAttachment={setFsmaPdfAttachment}
                  />
                </Suspense>
              )}
              {activeTab === "profile-terms-conditions" &&
                terms?.fsmaCertification !== "" && (
                  <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_200} aria-hidden="true" />}>
                    <InvestorTerms
                      saveTerms={saveTerms}
                      terms={terms || ({} as TermsFormValues)}
                      setCategoryRiskPdfAttachment={setCategoryRiskPdfAttachment}
                    />
                  </Suspense>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

InvestorProfile.displayName = "InvestorProfile";

export default InvestorProfile;
